import CheckIcon from "@mui/icons-material/Check";
import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  alpha,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  Button,
  DialogTitle,
  Spacer,
  TextField,
  TextFieldProps,
  Toolbar,
  useDialog,
} from "common";
import api from "app/api";
import { useEffect, useState } from "react";

export interface DirectorySelectorProps extends TextFieldProps {
  integration_id?: number | string;
}
export default function DirectorySelector({
  integration_id,
  ...rest
}: DirectorySelectorProps) {
  const [folders, setFolders] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPath, setCurrentPath] = useState<string>(
    (rest.value as string) || "/"
  );
  const [currentFolder, setCurrentFolder] = useState<string | null>(null);
  const [open, setOpen, dialog] = useDialog();

  function folderIn(folder: string) {
    setCurrentPath(folder);
    setCurrentFolder(null);
  }

  function folderUp() {
    const folderPaths = currentPath.split("/");
    folderPaths.pop();
    setCurrentPath(folderPaths.join("/"));
    setCurrentFolder(null);
  }

  async function loadFolders() {
    if (!integration_id || !open) {
      return;
    }
    setLoading(true);
    const { success, data, error } = await api.get(
      `/integrations/list-directories/${integration_id}?directory=${
        currentPath || ""
      }`
    );
    setLoading(false);
    if (success) {
      setFolders(data?.data || []);
    } else {
      setFolders([]);
    }
  }

  function getName(path: string) {
    const parts = path.split("/");
    return parts.pop();
  }
  function handleApply() {
    rest.onChange && rest.onChange({ target: { value: currentPath } });
    dialog.onClose && dialog.onClose({}, "backdropClick");
  }

  useEffect(() => {
    if (open) {
      loadFolders();
    }
  }, [open]);

  useEffect(() => {
    setCurrentPath((rest.value as string) || "/");
  }, [rest.value]);

  useEffect(() => {
    loadFolders();
  }, [currentPath]);

  return (
    <>
      <TextField
        {...rest}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setOpen(true)}
                disabled={rest.disabled}
              >
                <FolderIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Dialog {...dialog} fullWidth>
        <DialogTitle onClose={dialog.onClose}>Select Folder</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              pl: 1,
              pr: 1,
              borderRadius: "4px",
              border: theme =>
                `solid 1px ${alpha(theme.palette.text.primary, 0.5)}`,
            }}
          >
            <Toolbar>
              <Typography>{currentPath}</Typography>
              <Spacer right />
              <IconButton onClick={() => loadFolders()}>
                <RefreshIcon />
              </IconButton>
            </Toolbar>
          </Box>
          <List sx={{ position: "relative" }}>
            {loading && (
              <LinearProgress
                sx={{ position: "absolute", left: 0, top: 0, right: 0 }}
              />
            )}
            {currentPath !== "" && currentPath !== "/" && (
              <ListItemButton
                disabled={loading}
                divider
                //onClick={() => setCurrentFolder(null)}
                //onDoubleClick={() => folderUp()}
                onClick={() => folderUp()}
              >
                <ListItemIcon>
                  <DriveFolderUploadIcon />
                </ListItemIcon>
                <ListItemText primary=".." />
              </ListItemButton>
            )}
            {folders.map(folder => {
              return (
                <ListItemButton
                  disabled={loading}
                  key={folder}
                  divider
                  /*onClick={() => {
                    setCurrentFolder(folder);
                  }}
                  onDoubleClick={() => folderIn(folder)}*/
                  onClick={() => folderIn(folder)}
                >
                  <ListItemIcon>
                    {folder === currentFolder ? (
                      <FolderIcon />
                    ) : (
                      <FolderOutlinedIcon />
                    )}
                  </ListItemIcon>
                  <ListItemText primary={getName(folder)} />
                  <ListItemSecondaryAction>
                    {folder === currentFolder && <CheckIcon />}
                  </ListItemSecondaryAction>
                </ListItemButton>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Spacer right />
          <Button title="Cancel" variant="outlined" />
          <Button title="Apply" onClick={() => handleApply()} />
        </DialogActions>
      </Dialog>
    </>
  );
}
